import _ from 'lodash';

import { Cache, User } from '../utils/models';
import { getCurrentUser } from './authService';
import { AxiosError } from 'axios';

import httpService from './httpService';

const endpoint: string = '/project/cache';

export interface CachePayload {
	projectId: string;
	progressData: string;
	description?: string;
}

export interface CacheErrorResponse {
	error: true;
	message: string;
}
export interface CacheSuccessResponse {
	data: CachePayload;
}

interface CacheResult {
	error: boolean;
	data: Cache | null;
}

const getCacheFromResponse = (response: CacheSuccessResponse) => {
	if (response.data.progressData) {
		const item = JSON.parse(response.data.progressData);

		return {
			value: JSON.parse(item.value),
			timestamp: item.timestamp,
			user: JSON.parse(item.user),
		};
	}

	return null;
};

const generateCachePayload = (value: any): string => {
	const user = getCurrentUser() as User;

	const item = {
		value: JSON.stringify(value),
		timestamp: Date.now(),
		user: JSON.stringify(_.pick(user, ['id', 'firstName', 'lastName'])),
	};

	return JSON.stringify(item);
};

const store = (projectId: string, value: any, changeDescription?: string) => {
	const apiData: CachePayload = {
		projectId,
		progressData: generateCachePayload(value),
		description: changeDescription,
	};

	return httpService.post<CachePayload>(endpoint, apiData);
};

const restoreCachePayload = (cache: CachePayload) => {
	return httpService.post<CachePayload>(endpoint, cache);
};

const remove = (projectId: string) => {
	const apiData: CachePayload = {
		projectId,
		progressData: '',
	};

	return httpService.post<CachePayload>(endpoint, apiData);
};

const getError = (error: any) => {
	const response = { error: false, data: null };
	const cache = error.response?.data;

	if (cache) {
		response.error = cache.error ?? false ;
		response.data = cache.error ? null : getCacheFromResponse(cache) as any;
	} else {
		response.error = (error instanceof AxiosError) ? error.response?.status !== 404 : true;
	}

	return response;
}

const retrieve = async (projectId: string): Promise<CacheResult> => {
	try {
		const url: string = endpoint + '/' + projectId;

		const response = await httpService.get<CacheSuccessResponse>(url);
		return { error: false, data: getCacheFromResponse(response.data) }
	} catch (error: any) {
		return getError(error);
	}
};

const retrieveLastProgress = async (projectId: string): Promise<CacheResult> => {
	try {
		const url: string = '/project/project-progress/' + projectId;

		const response = await httpService.get<CacheSuccessResponse>(url);
		return { error: false, data: getCacheFromResponse(response.data) }
	} catch (error) {
		return getError(error);
	}
};

const getReportCacheProgress = (projectId: string) => {
	const user = getCurrentUser() as User;
	if (user.role.approveReports) return retrieveLastProgress(projectId);
	return retrieve(projectId);
};

const clearSingleProjectCache = (projectId: string) => {
	return httpService.delete('/project/single-cache/' + projectId);
};

export { clearSingleProjectCache, remove, store, getReportCacheProgress, generateCachePayload, restoreCachePayload, retrieve, retrieveLastProgress, getCacheFromResponse };
